// PhoneVerification2Phone.jsx
"use client"
import { useState, useEffect, useCallback } from "react"

import {
    Box,
    Button,
    Input,
    Text,
    VStack,
    HStack,
    Card,
    CardBody,
    Heading,
    FormControl,
    Badge,
    Icon,
    Container,
    useColorModeValue,
    InputGroup,
    InputLeftElement,
    Circle,
    Alert,
    AlertIcon,
    AlertTitle,
    AlertDescription,
    Spinner,
    Divider,
} from "@chakra-ui/react"
import { auth, setupRecaptcha, signInWithPhoneNumber } from "../../../firebaseConfig"
import { useLocation, useNavigate } from "react-router-dom"
import { useDispatch, useSelector } from "react-redux"
import { CheckPhoneVerifyAsync } from "../Slice/PhoneVerified/CheckPhoneVerifySlice"
import {
    checkPhoneVerificationPublicAsync,
    resetPhoneVerificationState,
} from "../Slice/PhoneVerificationSignup/PhoneVerificationSlice"
import {
    fetchBasicDetailsBySingleParamAsync,
    selectBasicDetailsBySingleParam,
} from "../../profile/BasicDetailsSingleParam/BasicDetailsSingleParamSlice"
import { fetchCookie } from "../../common/FetchCookie"

// Resend OTP cooldown time in seconds
const RESEND_COOLDOWN = 120

// Icons
const PhoneIcon = (props) => (
    <Icon viewBox="0 0 24 24" {...props}>
        <path
            fill="currentColor"
            d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"
        />
    </Icon>
)

const CheckIcon = (props) => (
    <Icon viewBox="0 0 24 24" {...props}>
        <path fill="currentColor" d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
    </Icon>
)

const ShieldIcon = (props) => (
    <Icon viewBox="0 0 24 24" {...props}>
        <path
            fill="currentColor"
            d="M12,1L3,5V11C3,16.55 6.84,21.74 12,23C17.16,21.74 21,16.55 21,11V5L12,1M12,7C13.4,7 14.8,8.6 14.8,10V11.5C15.4,11.5 16,12.4 16,13V16C16,17.4 15.4,18 14.8,18H9.2C8.6,18 8,17.4 8,16V13C8,12.4 8.6,11.5 9.2,11.5V10C9.2,8.6 10.6,7 12,7M12,8.2C11.2,8.2 10.5,8.7 10.5,10V11.5H13.5V10C13.5,8.7 12.8,8.2 12,8.2Z"
        />
    </Icon>
)

const RefreshIcon = (props) => (
    <Icon viewBox="0 0 24 24" {...props}>
        <path
            fill="currentColor"
            d="M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z"
        />
    </Icon>
)

const WarningIcon = (props) => (
    <Icon viewBox="0 0 24 24" {...props}>
        <path fill="currentColor" d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z" />
    </Icon>
)

const ArrowBackIcon = (props) => (
    <Icon viewBox="0 0 24 24" {...props}>
        <path fill="currentColor" d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
    </Icon>
)

export default function PhoneVerification2Phone() {
    const location = useLocation()
    const navigate = useNavigate()
    const dispatch = useDispatch()

    const [mobile, setMobile] = useState("")
    const [otp, setOtp] = useState("")
    const [confirmationResult, setConfirmationResult] = useState(null)
    const [error, setError] = useState("")
    const [loading, setLoading] = useState(false)
    const [successMessage, setSuccessMessage] = useState("")
    const [isPhoneVerified, setIsPhoneVerified] = useState(false)
    const [otpSent, setOtpSent] = useState(false)

    // Auth type state - 'form' or 'oauth'
    const [authType, setAuthType] = useState(null)
    const [isAuthTypeLoaded, setIsAuthTypeLoaded] = useState(false)
    const [isProfileLoaded, setIsProfileLoaded] = useState(false)

    // Phone number already exists state
    const [phoneAlreadyExists, setPhoneAlreadyExists] = useState(false)

    // Track if phone exists in profile
    const [hasPhoneInProfile, setHasPhoneInProfile] = useState(false)

    // Resend OTP timer states
    const [resendTimer, setResendTimer] = useState(0)
    const [canResend, setCanResend] = useState(false)

    // Get basic details from Redux
    const basicDetails = useSelector(selectBasicDetailsBySingleParam)

    // Phone verification state from Redux
    const {
        loading: phoneLoading,
        success: phoneVerifiedRedux,
        error: phoneReduxError,
    } = useSelector((state) => state.phoneVerificationPublic || {})

    // Color scheme
    const bgColor = useColorModeValue("gray.50", "gray.900")
    const cardBg = useColorModeValue("white", "gray.800")
    const borderColor = useColorModeValue("gray.200", "gray.600")
    const textColor = useColorModeValue("gray.800", "white")
    const mutedTextColor = useColorModeValue("gray.600", "gray.400")

    // Timer countdown effect
    useEffect(() => {
        let interval = null
        if (resendTimer > 0) {
            interval = setInterval(() => {
                setResendTimer((prev) => {
                    if (prev <= 1) {
                        setCanResend(true)
                        return 0
                    }
                    return prev - 1
                })
            }, 1000)
        }
        return () => {
            if (interval) clearInterval(interval)
        }
    }, [resendTimer])

    // Format timer display
    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60)
        const secs = seconds % 60
        return `${mins}:${secs.toString().padStart(2, "0")}`
    }

    // Fetch auth type and user data from cookie on mount
    useEffect(() => {
        const getAuthType = async () => {
            try {
                const userData = await fetchCookie()
                const registerType = userData?.user?.register_type || userData?.register_type

                // Handle both 'oauth' and 'google' as OAuth type
                if (registerType === "oauth" || registerType === "google") {
                    setAuthType("oauth")
                } else {
                    setAuthType("form")
                }
            } catch (error) {
                console.error("Error fetching auth type:", error)
                setAuthType("form") // Default to 'form' on error
            } finally {
                setIsAuthTypeLoaded(true)
            }
        }
        getAuthType()
    }, [])

    // Setup recaptcha when component mounts
    useEffect(() => {
        const initializeRecaptcha = () => {
            try {
                setupRecaptcha()
            } catch (error) {
                console.error("Error initializing recaptcha:", error)
            }
        }
        const timer = setTimeout(initializeRecaptcha, 100)
        return () => {
            clearTimeout(timer)
            if (window.recaptchaVerifier) {
                window.recaptchaVerifier.clear()
                window.recaptchaVerifier = null
            }
        }
    }, [])

    // Reset verification states and fetch profile on component mount
    useEffect(() => {
        dispatch(resetPhoneVerificationState())
        dispatch(fetchBasicDetailsBySingleParamAsync())
    }, [dispatch])

    // Prefill phone from basicDetails (profile API)
    useEffect(() => {
        if (basicDetails !== null && basicDetails !== undefined) {
            // Check if phone exists and is not null/empty
            const phoneFromProfile = basicDetails.phone || basicDetails.mobile
            const hasPhone =
                phoneFromProfile &&
                phoneFromProfile.toString().trim() !== "" &&
                phoneFromProfile.toString() !== "null"

            setHasPhoneInProfile(hasPhone)

            if (hasPhone) {
                setMobile(String(phoneFromProfile))
            }

            // Check if already verified from API response
            if (basicDetails.phone_verify === 1) {
                setIsPhoneVerified(true)
            }

            setIsProfileLoaded(true)
        }
    }, [basicDetails])

    // Handle phone verification success - redirect to candidate dashboard
    useEffect(() => {
        if (phoneVerifiedRedux) {
            setIsPhoneVerified(true)
            setSuccessMessage("Phone verification successful!")
            setOtp("")
            setTimeout(() => {
                // Always redirect to candidate dashboard for this component
                navigate("/candidate-dashboard")
            }, 1500)
        }
    }, [phoneVerifiedRedux, navigate])

    // Handle phone verification error
    useEffect(() => {
        if (phoneReduxError) {
            setError(phoneReduxError)
        }
    }, [phoneReduxError])

    const formatPhoneNumber = (phone) => {
        if (!phone) return ""
        const cleaned = phone.toString().replace(/\D/g, "")
        if (phone.toString().startsWith("+91")) {
            return phone.toString()
        }
        if (cleaned.length === 10) {
            return `+91${cleaned}`
        }
        if (cleaned.startsWith("91") && cleaned.length === 12) {
            return `+${cleaned}`
        }
        return `+91${cleaned}`
    }

    const validatePhoneNumber = (phone) => {
        if (!phone) return false
        const cleaned = phone.toString().replace(/\D/g, "")
        return cleaned.length === 10 || (cleaned.length === 12 && cleaned.startsWith("91"))
    }

    // Helper function to send OTP via Firebase
    const sendOtpViaFirebase = async (phoneNumber) => {
        try {
            if (!window.recaptchaVerifier) {
                setupRecaptcha()
                await new Promise((resolve) => setTimeout(resolve, 500))
            }

            const appVerifier = window.recaptchaVerifier
            if (!appVerifier) {
                throw new Error("Recaptcha verifier not initialized")
            }

            const confirmation = await signInWithPhoneNumber(auth, phoneNumber, appVerifier)
            setConfirmationResult(confirmation)
            setSuccessMessage("OTP sent successfully!")
            setOtpSent(true)
            setPhoneAlreadyExists(false)

            // Start the resend timer (120 seconds)
            setResendTimer(RESEND_COOLDOWN)
            setCanResend(false)
        } catch (otpError) {
            console.error("OTP Error:", otpError)
            if (otpError.code === "auth/invalid-phone-number") {
                setError("Invalid phone number format. Please check and try again.")
            } else if (otpError.code === "auth/too-many-requests") {
                setError("Too many requests. Please try again later.")
            } else if (otpError.code === "auth/quota-exceeded") {
                setError("SMS quota exceeded. Please try again later.")
            } else {
                setError("Error sending OTP. Please try again.")
            }

            if (window.recaptchaVerifier) {
                window.recaptchaVerifier.clear()
                window.recaptchaVerifier = null
            }

            try {
                setupRecaptcha()
            } catch (setupError) {
                console.error("Error reinitializing recaptcha:", setupError)
            }
        }
    }

    // Handle phone number change
    const handlePhoneChange = (e) => {
        const newPhone = e.target.value
        setMobile(newPhone)
        // Reset states when user changes phone number
        setError("")
        setSuccessMessage("")
        setPhoneAlreadyExists(false)
        setOtpSent(false)
        setConfirmationResult(null)
        setOtp("")
        setResendTimer(0)
        setCanResend(false)
    }

    const sendOtp = async (e) => {
        e.preventDefault()
        setError("")
        setSuccessMessage("")
        setPhoneAlreadyExists(false)
        setLoading(true)
        const latestMobile = mobile.toString().trim()

        if (!latestMobile) {
            setError("Please enter a valid phone number.")
            setLoading(false)
            return
        }

        if (!validatePhoneNumber(latestMobile)) {
            setError("Please enter a valid 10-digit phone number.")
            setLoading(false)
            return
        }

        const phoneNumber = formatPhoneNumber(latestMobile)

        // Determine the flow based on whether phone exists in profile and auth type
        if (!hasPhoneInProfile) {
            // No phone in profile - user is entering a new number
            try {
                const result = await dispatch(CheckPhoneVerifyAsync(latestMobile)).unwrap()

                if (result?.code === 200) {
                    setPhoneAlreadyExists(true)
                    setError(
                        "This phone number is already registered with another account. Please enter a different phone number."
                    )
                    setLoading(false)
                    return
                }
            } catch (err) {
                console.warn("Phone not found in database, proceeding with OTP:", err.message)
                setPhoneAlreadyExists(false)
            }

            try {
                await sendOtpViaFirebase(phoneNumber)
            } catch (err) {
                console.error("Error sending OTP:", err)
            }
        } else if (authType === "form") {
            try {
                await sendOtpViaFirebase(phoneNumber)
            } catch (err) {
                console.error("Error sending OTP for form signup:", err)
            }
        } else if (authType === "oauth") {
            try {
                const result = await dispatch(CheckPhoneVerifyAsync(latestMobile)).unwrap()

                if (result?.code === 200) {
                    setPhoneAlreadyExists(true)
                    setError(
                        "This phone number is already registered with another account. Please enter a different phone number."
                    )
                    setLoading(false)
                    return
                }
            } catch (err) {
                console.warn("Phone not verified in database, proceeding with OTP:", err.message)
                setPhoneAlreadyExists(false)
            }

            try {
                await sendOtpViaFirebase(phoneNumber)
            } catch (err) {
                console.error("Error sending OTP for OAuth signup:", err)
            }
        } else {
            try {
                await sendOtpViaFirebase(phoneNumber)
            } catch (err) {
                console.error("Error sending OTP:", err)
            }
        }

        setLoading(false)
    }

    // Resend OTP function
    const resendOtp = async (e) => {
        e.preventDefault()
        if (!canResend) return

        setError("")
        setSuccessMessage("")
        setLoading(true)
        setOtp("") // Clear the OTP input

        const phoneNumber = formatPhoneNumber(mobile.toString().trim())

        // Clear and reinitialize recaptcha for resend
        if (window.recaptchaVerifier) {
            window.recaptchaVerifier.clear()
            window.recaptchaVerifier = null
        }

        try {
            setupRecaptcha()
            await new Promise((resolve) => setTimeout(resolve, 500))
            await sendOtpViaFirebase(phoneNumber)
        } catch (err) {
            console.error("Error resending OTP:", err)
            setError("Failed to resend OTP. Please try again.")
        }

        setLoading(false)
    }

    const verifyOtp = async (e) => {
        e.preventDefault()
        setError("")
        setSuccessMessage("")
        setLoading(true)

        if (!otp.trim()) {
            setError("Please enter the OTP.")
            setLoading(false)
            return
        }

        if (!confirmationResult) {
            setError("Please request OTP first.")
            setLoading(false)
            return
        }

        try {
            const result = await confirmationResult.confirm(otp.trim())

            const phoneNumber = formatPhoneNumber(mobile.toString().trim())

            await dispatch(
                checkPhoneVerificationPublicAsync({
                    mobile: phoneNumber,
                    code: otp.trim(),
                })
            ).unwrap()
        } catch (err) {
            console.error("OTP verification error:", err)
            if (err.code === "auth/invalid-verification-code") {
                setError("Invalid OTP. Please check and try again.")
            } else if (err.code === "auth/code-expired") {
                setError("OTP has expired. Please request a new one.")
            } else {
                setError(err.message || "Invalid OTP. Please try again.")
            }
        } finally {
            setLoading(false)
        }
    }

    // Go back to dashboard
    const handleGoBack = () => {
        navigate("/candidate-dashboard")
    }

    const isPhoneFieldEditable = () => {
        if (!hasPhoneInProfile) return true
        if (authType === "oauth") return true
        if (phoneAlreadyExists) return true
        return false
    }

    const isPhoneFieldDisabled = () => {
        if (loading) return true
        if (otpSent && !phoneAlreadyExists) return true
        if (isPhoneVerified) return true
        if (!isPhoneFieldEditable()) return true
        return false
    }

    // Get helper text based on conditions
    const getHelperText = () => {
        if (phoneAlreadyExists) {
            return "This number is already registered. Please enter a different number."
        }
        if (!hasPhoneInProfile) {
            return "Enter your 10-digit mobile number to add and verify"
        }
        if (authType === "form" && hasPhoneInProfile) {
            return "Phone number from your registration (cannot be changed)"
        }
        if (authType === "oauth") {
            return "Enter your 10-digit mobile number without country code"
        }
        return "Enter 10-digit mobile number without country code"
    }

    // Show loading state while fetching auth type or profile
    if (!isAuthTypeLoaded || !isProfileLoaded) {
        return (
            <Box minH="100vh" bg={bgColor} display="flex" alignItems="center" justifyContent="center" p={4}>
                <VStack spacing={4}>
                    <Spinner size="xl" color="blue.500" thickness="4px" speed="0.65s" />
                    <Text color={mutedTextColor}>Loading...</Text>
                </VStack>
            </Box>
        )
    }

    return (
        <Box minH="100vh" bg={bgColor} display="flex" alignItems="center" justifyContent="center" p={4}>
            <Container maxW="xl">
                {/* Back Button */}
                <Button
                    leftIcon={<ArrowBackIcon w={5} h={5} />}
                    variant="ghost"
                    colorScheme="gray"
                    mb={6}
                    onClick={handleGoBack}
                    color={mutedTextColor}
                    _hover={{ bg: "gray.100", color: textColor }}
                >
                    Back to Dashboard
                </Button>

                {/* Header Section */}
                <VStack spacing={4} align="center" mb={8}>
                    <Circle size="80px" bg="blue.100" color="blue.600">
                        <ShieldIcon w={10} h={10} />
                    </Circle>
                    <VStack spacing={1}>
                        <Heading size="lg" color={textColor} fontWeight="600">
                            Phone Verification
                        </Heading>
                        <Text fontSize="sm" color={mutedTextColor} textAlign="center">
                            {hasPhoneInProfile
                                ? "Verify your phone number to secure your account"
                                : "Add and verify your phone number to secure your account"}
                        </Text>
                    </VStack>
                </VStack>

                {/* Main Card */}
                <Card bg={cardBg} shadow="xl" borderRadius="2xl" border="1px" borderColor={borderColor} overflow="hidden">
                    {/* Card Header with gradient */}
                    <Box bg="linear-gradient(135deg, #667eea 0%, #764ba2 100%)" px={6} py={4}>
                        <VStack spacing={1} align="start">
                            <Heading size="sm" color="white" fontWeight="600">
                                {hasPhoneInProfile ? "Verify Your Phone Number" : "Add Your Phone Number"}
                            </Heading>
                            <Text fontSize="xs" color="whiteAlpha.800">
                                {hasPhoneInProfile
                                    ? "We'll send a verification code to your phone"
                                    : "Enter your phone number and we'll send a verification code"}
                            </Text>
                        </VStack>
                    </Box>

                    <CardBody p={6}>
                        <VStack spacing={5}>
                            {/* Phone Header */}
                            <HStack w="full" justify="space-between" align="center">
                                <HStack spacing={2}>
                                    <Circle
                                        size="40px"
                                        bg={
                                            isPhoneVerified
                                                ? "green.100"
                                                : phoneAlreadyExists
                                                    ? "red.100"
                                                    : !hasPhoneInProfile
                                                        ? "blue.100"
                                                        : "gray.100"
                                        }
                                        color={
                                            isPhoneVerified
                                                ? "green.600"
                                                : phoneAlreadyExists
                                                    ? "red.600"
                                                    : !hasPhoneInProfile
                                                        ? "blue.600"
                                                        : mutedTextColor
                                        }
                                    >
                                        <Icon as={isPhoneVerified ? CheckIcon : PhoneIcon} w={5} h={5} />
                                    </Circle>
                                    <VStack align="start" spacing={0}>
                                        <Text fontWeight="500" color={textColor} fontSize="sm">
                                            Phone Number
                                        </Text>
                                        {!hasPhoneInProfile && !isPhoneVerified && (
                                            <Text fontSize="xs" color="blue.500">
                                                (Not added yet)
                                            </Text>
                                        )}
                                    </VStack>
                                </HStack>
                                {isPhoneVerified && (
                                    <Badge colorScheme="green" variant="subtle" borderRadius="full" fontSize="xs" px={2}>
                                        <HStack spacing={1}>
                                            <CheckIcon w={3} h={3} />
                                            <Text>Verified</Text>
                                        </HStack>
                                    </Badge>
                                )}
                            </HStack>

                            {/* Phone Input */}
                            <FormControl>
                                <InputGroup size="lg">
                                    <InputLeftElement pointerEvents="none">
                                        <PhoneIcon color={mutedTextColor} w={5} h={5} />
                                    </InputLeftElement>
                                    <Input
                                        type="tel"
                                        value={mobile}
                                        onChange={handlePhoneChange}
                                        placeholder="Enter your phone number (e.g. 9876543210)"
                                        borderColor={phoneAlreadyExists ? "red.500" : borderColor}
                                        _hover={{ borderColor: phoneAlreadyExists ? "red.500" : "gray.300" }}
                                        _focus={{
                                            borderColor: phoneAlreadyExists ? "red.500" : "blue.400",
                                            boxShadow: phoneAlreadyExists ? "0 0 0 1px #E53E3E" : "0 0 0 1px #4299e1",
                                        }}
                                        isDisabled={isPhoneFieldDisabled()}
                                        readOnly={!isPhoneFieldEditable() && !phoneAlreadyExists}
                                        bg={isPhoneFieldDisabled() ? "gray.50" : "white"}
                                    />
                                </InputGroup>
                                <Text fontSize="xs" color={phoneAlreadyExists ? "red.500" : mutedTextColor} mt={2}>
                                    {getHelperText()}
                                </Text>
                            </FormControl>

                            {/* No Phone Number Info */}
                            {!hasPhoneInProfile && !isPhoneVerified && !otpSent && (
                                <Alert status="info" borderRadius="md" py={3}>
                                    <AlertIcon as={PhoneIcon} />
                                    <Box>
                                        <AlertTitle fontSize="sm">No Phone Number Found</AlertTitle>
                                        <AlertDescription fontSize="xs">
                                            Your account doesn't have a phone number yet. Please enter your phone number
                                            below to add and verify it.
                                        </AlertDescription>
                                    </Box>
                                </Alert>
                            )}

                            {/* Phone Already Exists Warning */}
                            {phoneAlreadyExists && (
                                <Alert status="warning" borderRadius="md" py={3}>
                                    <AlertIcon as={WarningIcon} />
                                    <Box>
                                        <AlertTitle fontSize="sm">Phone Number Already Registered</AlertTitle>
                                        <AlertDescription fontSize="xs">
                                            This phone number is already associated with another account. Please enter a
                                            different phone number to continue.
                                        </AlertDescription>
                                    </Box>
                                </Alert>
                            )}

                            {/* Already Verified Message */}
                            {isPhoneVerified && !phoneVerifiedRedux && (
                                <Alert status="success" borderRadius="md" py={3}>
                                    <AlertIcon as={CheckIcon} />
                                    <Box>
                                        <AlertTitle fontSize="sm">Phone Already Verified</AlertTitle>
                                        <AlertDescription fontSize="xs">
                                            Your phone number is already verified. You can go back to the dashboard.
                                        </AlertDescription>
                                    </Box>
                                </Alert>
                            )}

                            {/* Send OTP Button */}
                            {!isPhoneVerified && (
                                <Button
                                    colorScheme={phoneAlreadyExists ? "orange" : "blue"}
                                    size="lg"
                                    w="full"
                                    onClick={sendOtp}
                                    isLoading={loading}
                                    loadingText={!hasPhoneInProfile ? "Checking & Sending OTP..." : "Sending OTP..."}
                                    isDisabled={
                                        loading ||
                                        (otpSent && !phoneAlreadyExists) ||
                                        isPhoneVerified ||
                                        !mobile.toString().trim()
                                    }
                                >
                                    {isPhoneVerified
                                        ? "Verified"
                                        : otpSent && !phoneAlreadyExists
                                            ? "OTP Sent"
                                            : phoneAlreadyExists
                                                ? "Try Different Number"
                                                : "Send Verification Code"}
                                </Button>
                            )}

                            {/* OTP Input and Verify */}
                            {otpSent && !isPhoneVerified && !phoneAlreadyExists && (
                                <>
                                    <Divider />
                                    <Text fontSize="sm" color={mutedTextColor} textAlign="center">
                                        Enter verification code
                                    </Text>

                                    <Input
                                        type="text"
                                        value={otp}
                                        onChange={(e) => setOtp(e.target.value)}
                                        placeholder="Enter the 6-digit OTP"
                                        textAlign="center"
                                        size="lg"
                                        maxLength={6}
                                        letterSpacing="0.5em"
                                        fontWeight="bold"
                                        fontSize="xl"
                                        isDisabled={loading}
                                        borderColor={borderColor}
                                        _focus={{ borderColor: "blue.400", boxShadow: "0 0 0 1px #4299e1" }}
                                    />

                                    {/* Resend OTP Section */}
                                    <HStack w="full" justify="space-between" align="center">
                                        <Text fontSize="sm" color={mutedTextColor}>
                                            {resendTimer > 0 ? (
                                                <>
                                                    Resend OTP in{" "}
                                                    <Text as="span" fontWeight="bold" color="blue.500">
                                                        {formatTime(resendTimer)}
                                                    </Text>
                                                </>
                                            ) : (
                                                "Didn't receive the code?"
                                            )}
                                        </Text>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            leftIcon={<RefreshIcon w={4} h={4} className={loading ? "animate-spin" : ""} />}
                                            onClick={resendOtp}
                                            isDisabled={!canResend || loading}
                                            color={canResend && !loading ? "blue.500" : "gray.400"}
                                            _hover={{ bg: canResend ? "blue.50" : "transparent" }}
                                        >
                                            Resend OTP
                                        </Button>
                                    </HStack>

                                    <Button
                                        colorScheme="green"
                                        size="lg"
                                        w="full"
                                        onClick={verifyOtp}
                                        isLoading={loading}
                                        loadingText="Verifying..."
                                    >
                                        Verify OTP
                                    </Button>
                                </>
                            )}

                            {/* Error/Success Messages */}
                            {error && !phoneAlreadyExists && (
                                <Alert status="error" borderRadius="md" py={2}>
                                    <AlertIcon />
                                    <Text fontSize="sm">{error}</Text>
                                </Alert>
                            )}

                            {successMessage && (
                                <Alert status="success" borderRadius="md" py={2}>
                                    <AlertIcon />
                                    <Text fontSize="sm">{successMessage}</Text>
                                </Alert>
                            )}

                            {/* Divider */}
                            <Divider />

                            {/* Go Back Button */}
                            <Button
                                variant="outline"
                                colorScheme="gray"
                                size="lg"
                                w="full"
                                leftIcon={<ArrowBackIcon w={5} h={5} />}
                                onClick={handleGoBack}
                            >
                                Go Back to Dashboard
                            </Button>
                        </VStack>
                    </CardBody>
                </Card>

                {/* Recaptcha Container */}
                <div id="recaptcha-container" style={{ marginTop: "20px" }}></div>
            </Container>
        </Box>
    )
}